import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../..";
import { useRouter } from "next/router";
import NextLink from "next/link";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import Masonry from "react-masonry-css";
import useSWRInfinite from "swr/infinite";
import clsx from "clsx";
import Tags from "./Tag";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var apiEndpoint = process.env.NEXT_PUBLIC_API_URL || "https://ccq-api.vercel.app";
dayjs.extend(localizedFormat);
export var BlogPosts = _ref => {
  var {
    per_page = 30,
    loadDraft = false
  } = _ref;
  var [tag, setTag] = useState({
    name: "All Articles",
    id: "all"
  });
  var [total, setTotal] = useState(0);
  var [postsLoading, setPostsLoading] = useState(false);
  var [allPostsLoaded, setAllPostsLoaded] = useState(false);
  var {
    ref: loadMoreRef,
    isVisible: isloadMoreVisible
  } = useOnScreen();
  var variant = getCurrentWebsiteVariant();
  useEffect(() => {
    if (isloadMoreVisible && !postsLoading) {
      setSize(size + 1);
    }
  }, [isloadMoreVisible]);
  var selectTag = useCallback(selectedTag => {
    if (selectedTag !== tag) {
      setTag(selectedTag);
      setAllPostsLoaded(false);
    }
  }, [tag, setTag, setAllPostsLoaded]);
  var getKey = useCallback((pageIndex, previousPageData) => {
    var _tag$id;

    // reached the end
    if (previousPageData && previousPageData != null && previousPageData.stories && (!(previousPageData != null && previousPageData.stories.length) || (previousPageData == null ? void 0 : previousPageData.stories.length) < per_page)) {
      setAllPostsLoaded(true);
      return null;
    }

    return apiEndpoint + "/api/v1/blog/" + variant + "?page=" + (pageIndex + 1) + "&per_page=" + per_page + "&version=" + (loadDraft ? "draft" : "published") + "&with_tag=" + ((_tag$id = tag == null ? void 0 : tag.id) != null ? _tag$id : "");
  }, [variant, tag, loadDraft, setAllPostsLoaded]);
  var blogsFetcher = useCallback(url => {
    setPostsLoading(true);
    return fetch(url).then(res => res.json()).then(res => {
      setAllPostsLoaded(!res.stories.length || (res == null ? void 0 : res.stories.length) > per_page);
      setTotal(res.total || 0);
      return res;
    }).finally(() => setPostsLoading(false));
  }, [setPostsLoading]);
  var {
    data,
    size,
    error: blogsError,
    setSize
  } = useSWRInfinite(getKey, blogsFetcher, {
    revalidateFirstPage: true,
    revalidateAll: false
  });
  return blogsError ? /*#__PURE__*/_jsx("div", {}) : /*#__PURE__*/_jsxs("div", {
    className: "relative pb-28",
    children: [/*#__PURE__*/_jsx(Tags, {
      variant: variant,
      tag: tag,
      setTag: selectTag
    }), data ? total > 0 ? /*#__PURE__*/_jsx(Masonry, {
      breakpointCols: {
        default: 3,
        1100: 3,
        700: 2,
        500: 1
      },
      className: "mt-3 pt-6 border-t-2 border-ccq-yellow flex w-auto",
      columnClassName: "pl-4 bg-clip-padding",
      children: data.map(_ref2 => {
        var {
          stories
        } = _ref2;
        return stories.map(story => /*#__PURE__*/_jsx(ArticleCard, {
          story: story
        }, story.full_slug));
      })
    }) : /*#__PURE__*/_jsx("div", {
      className: "mt-5 text-center",
      children: /*#__PURE__*/_jsx(Button, {
        onClick: () => selectTag({
          id: "all",
          name: ""
        }),
        color: "primary",
        children: "Show all articles"
      })
    }) : /*#__PURE__*/_jsx("div", {
      className: "flex items-center justify-center h-28",
      children: /*#__PURE__*/_jsx("div", {
        style: {
          borderTopColor: "transparent"
        },
        className: "w-8 h-8 border-2 border-primary-400 border-solid rounded-full animate-spin"
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: clsx("text-center py-5", {
        hidden: allPostsLoaded || !data || postsLoading
      }),
      children: [/*#__PURE__*/_jsx("div", {
        className: "absolute bottom-1/4 w-full h-1",
        ref: loadMoreRef
      }), postsLoading ? /*#__PURE__*/_jsx("span", {
        children: "Loading more..."
      }) : /*#__PURE__*/_jsx(Button, {
        color: "primary",
        onClick: () => setSize(size + 1),
        children: "Load More"
      })]
    })]
  });
};

function ArticleCard(_ref3) {
  var _story$content, _story$content$meta, _story$content2, _story$content2$meta, _story$content3, _story$content3$meta;

  var {
    story
  } = _ref3;
  return /*#__PURE__*/_jsx(NextLink, {
    href: cleanHref(story.full_slug),
    children: /*#__PURE__*/_jsxs("a", {
      "data-chromatic": "ignore",
      className: "shadow-md mb-4 bg-[#fcfcfc] border-b-2 border-ccq-yellow rounded hover:shadow-lg flex flex-col justify-between",
      children: [((_story$content = story.content) == null ? void 0 : (_story$content$meta = _story$content.meta) == null ? void 0 : _story$content$meta.og_image) && /*#__PURE__*/_jsx("img", {
        src: (_story$content2 = story.content) == null ? void 0 : (_story$content2$meta = _story$content2.meta) == null ? void 0 : _story$content2$meta.og_image,
        className: "w-full"
      }), /*#__PURE__*/_jsxs("div", {
        className: "flex flex-col p-3",
        children: [/*#__PURE__*/_jsx("h4", {
          className: "text-lg mb-0",
          children: story == null ? void 0 : story.name
        }), /*#__PURE__*/_jsx("small", {
          className: "mb-4 text-sm ",
          children: dayjs(story.first_published_at).format("LL")
        }), /*#__PURE__*/_jsx("p", {
          children: (_story$content3 = story.content) == null ? void 0 : (_story$content3$meta = _story$content3.meta) == null ? void 0 : _story$content3$meta.description
        }), /*#__PURE__*/_jsx("div", {
          className: "underline text-ccq-dark-blue",
          children: "Read more"
        })]
      })]
    })
  });
}

var cleanHref = function cleanHref(href, variant) {
  if (variant === void 0) {
    variant = process.env.NEXT_PUBLIC_VARIANT;
  }

  if (!href) return "";
  var url = href;

  if (url.startsWith("/")) {
    url = url.slice(1);
  }

  if (variant) {
    if (url.startsWith(variant)) {
      url = url.replace(variant, "");
      return url;
    }
  }

  return "/" + url;
};

function useOnScreen(rootMargin) {
  if (rootMargin === void 0) {
    rootMargin = "0px";
  }

  // State and setter for storing whether element is visible
  var ref = useRef(null);
  var [isIntersecting, setIntersecting] = useState(false);
  useEffect(() => {
    var observer = new IntersectionObserver(_ref4 => {
      var [entry] = _ref4;
      // Update our state when observer callback fires
      setIntersecting(entry.isIntersecting);
    }, {
      rootMargin
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return {
    ref,
    isVisible: isIntersecting
  };
}

function getCurrentWebsiteVariant() {
  var variant = process.env.NEXT_PUBLIC_VARIANT;
  var router = useRouter();

  if (!variant) {
    var slug = router.asPath.split("?")[0].split("/").filter(v => v);
    if (slug.length) variant = slug[0];else variant = "dev";
  }

  return variant;
}
//# sourceMappingURL=blog-posts.js.map